// What the page says when the map cannot be drawn.
//
// On 25 Aug 2026 both city sites came up as a title, a NOW card and a black
// rectangle. The console held the whole answer and the page held none of it:
//
//   Could not create a WebGL context, VENDOR = 0x1414, DEVICE = 0x008c,
//   GL_RENDERER = ANGLE (Microsoft, Microsoft Basic Render Driver ...),
//   Sandboxed = yes
//
// Microsoft Basic Render Driver is Windows' software adapter, used when no GPU
// driver is exposed — a virtual machine, a remote desktop session, a graphics
// driver mid-update. MapLibre asks for a context, does not get one, and
// `new Map()` throws. main() is async and nothing caught it, so the rejection
// went to the console and the reader kept a black screen indefinitely.
//
// **The black is not a failed render. It is the absence of any render.** #map
// is `position: absolute; inset: 0` over the page background, so a map that
// never paints and a map of somewhere unlit look exactly alike, and every
// diagnosis a reader can make from the outside is wrong. That asymmetry is the
// whole reason this file exists: index.html has prose for a browser with
// JavaScript switched off, and had nothing at all for a browser that runs the
// script and cannot draw.
//
// Deliberately not a fallback map, and deliberately not a retry. There is
// nothing to retry — the adapter will not change while the tab is open — and a
// static picture of a city is not this site, which is live or it is nothing.
// What it does is name the cause, say the one thing that usually fixes it, and
// point at the three pages that need no WebGL. That is the same job the
// <noscript> block does, for the failure one step further in.



// Imported, no longer local: escape.ts is pure — no import.meta.env, no
// imports at all — so `node --test` loads it happily, which was the whole
// reason this module used to carry its own copy (#76).
import { escapeHtml } from './escape.ts';

/** The minimum of a canvas this probe needs, so it can be tested in Node. */
export interface ContextSource {
  getContext(id: string, attributes?: unknown): unknown;
}

/**
 * Can this browser give us a WebGL context at all?
 *
 * Only ever called on the failure path, after MapLibre has already thrown, to
 * choose which message to show. That ordering is not incidental: probing up
 * front would spend one of the browser's handful of live WebGL contexts on
 * every visit that was going to work anyway, and MapLibre wants the next one.
 * Asking only once the answer matters costs nothing and cannot compete with
 * the map for a slot that no longer exists.
 *
 * webgl2 then webgl, because MapLibre 5 prefers the first and accepts the
 * second, so either one means the failure was something other than this.
 */
export function webglAvailable(make: () => ContextSource | null): boolean {
  try {
    const source = make();
    if (!source) return false;
    return Boolean(source.getContext('webgl2') ?? source.getContext('webgl'));
  } catch {
    // A browser that throws from getContext has no WebGL either. This is the
    // failure path already; it must not become a second failure.
    return false;
  }
}

/** The default probe: a canvas that is never attached to the document. */
export const canvasProbe = (): ContextSource | null =>
  (typeof document === 'undefined' ? null : document.createElement('canvas'));

/**
 * The message, as markup.
 *
 * `kind` says which of the two things went wrong, because they want different
 * sentences: no WebGL is the reader's machine and has a fix they can apply,
 * while a map engine that failed with WebGL present is ours and does not.
 * Telling somebody to switch on hardware acceleration when that was never the
 * problem sends them to fix a working setting.
 */
export function mapFailureHtml(city: string, kind: 'no-webgl' | 'other'): string {
  const name = escapeHtml(city || 'This map');
  const cause = kind === 'no-webgl'
    ? `<p>
         The map is drawn by your graphics hardware, using WebGL, and this
         browser could not start it. Nothing is wrong with the site, and there
         is nothing to wait for — this is your browser and this machine.
       </p>
       <p>
         <strong>If the map worked here before, restart the computer first.</strong>
         A graphics driver that has crashed or is part-way through an update
         drops the browser onto a software renderer, and a restart is the whole
         fix. Chrome and Edge will say which they are using at
         <strong>chrome://gpu</strong>.
       </p>
       <p>
         If a restart changes nothing, check
         <strong>Settings → System → “Use graphics acceleration when
         available”</strong> in Chrome or Edge, or
         <strong>Settings → General → Performance</strong> in Firefox, and
         restart the browser. Failing that, the site works normally on another
         machine or phone.
       </p>`
    : `<p>
         The map engine failed to start. WebGL is available here, so this is a
         fault at our end rather than a setting of yours. Reloading the page is
         worth one try; if it keeps happening we would like to know.
       </p>`;

  return `
    <h2>${name} cannot draw the map in this browser</h2>
    ${cause}
    <p>
      These pages need none of it: <a href="/status">the feed status page</a>
      says when every feed behind this map last answered,
      <a href="https://scot.live/sources">the data sources catalogue</a> lists
      every feed behind every city and under what licence, and
      <a href="/faq">the questions page</a> explains why a layer might be
      switched off and what the map does not know.
    </p>
    <p>
      Other cities, and what this project is:
      <a href="https://scot.live/">scot.live</a>.
    </p>`;
}

/**
 * Replace the map with the message.
 *
 * `no-map` on the body rather than markup inside #map, because the page stops
 * being a map at this point and should stop behaving like one: #map, the dock
 * and the floating boxes are all empty and never fill, and #hud is absolutely
 * positioned over a viewport-sized element it would otherwise share with this
 * text. Laid out over the top of each other, on a phone, the Now card lands in
 * the middle of the second paragraph — which the first screenshot of this panel
 * showed and no amount of reading the stylesheet did.
 *
 * The alternative was a top padding big enough to clear #hud, and it is the
 * wrong shape: the card is collapsible, so its height is not a number this file
 * can know. A document that scrolls needs no such number.
 */
export function showMapFailure(
  body: HTMLElement | null,
  city: string,
  probe: () => ContextSource | null = canvasProbe,
): void {
  if (!body) return;
  const kind = webglAvailable(probe) ? 'other' : 'no-webgl';
  const panel = document.createElement('section');
  panel.id = 'no-webgl';
  panel.setAttribute('role', 'alert');
  panel.innerHTML = mapFailureHtml(city, kind);
  body.classList.add('no-map');
  body.appendChild(panel);
}
